import React, { useState, useEffect } from 'react';
import { Card, CardBody, Typography, Input, Button } from "@material-tailwind/react";
import axios from 'axios';
import { FaTrash, FaPlus, FaTimes, FaPenAlt } from 'react-icons/fa';
import { useAuth } from '../AuthContext';
const { rootUrl } = require('../../../config/config');

const UnitsOfMeasure = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    code: '',
    name: '',
    abbreviation: ''
  });
  const { user } = useAuth();

  // Fetch all units of measure
  const fetchItems = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken') || 'default-token';
      const response = await axios.get(`${rootUrl}/parts/units`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(response.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch units of measure.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  // Start editing a row - populate form with item data
  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      code: item.code || '',
      name: item.name || '',
      abbreviation: item.abbreviation || ''
    });
    // Scroll to top to show the form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Cancel editing/adding - clear form
  const cancelForm = () => {
    setEditingId(null);
    setForm({
      code: '',
      name: '',
      abbreviation: ''
    });
    setError('');
  };

  // Delete an item
  const deleteItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this unit of measure?')) return;
    try {
      const token = localStorage.getItem('authToken') || 'default-token';
      await axios.delete(`${rootUrl}/parts/units/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchItems();
    } catch (err) {
      setError('Failed to delete unit of measure.');
      console.error(err);
    }
  };

  // Handle form submission for both add and edit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.name.trim() || !form.abbreviation.trim()) {
      setError('Code, name, and abbreviation are required.');
      return;
    }

    try {
      const token = localStorage.getItem('authToken') || 'default-token';

      if (editingId) {
        // Update existing item
        await axios.put(
          `${rootUrl}/parts/units/${editingId}`,
          {
            ...form,
            edit_by: user?.username || 'system'
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } else {
        // Add new item
        await axios.post(
          `${rootUrl}/parts/units`,
          {
            ...form,
            input_by: user?.username || 'system'
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }

      cancelForm();
      fetchItems();
    } catch (err) {
      setError(editingId
        ? 'Failed to update unit of measure.'
        : 'Failed to add unit of measure.');
      console.error(err);
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto mt-6 px-2 sm:px-4">
      <Card className="border-t">
        <CardBody className="p-3 sm:p-6">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4 sm:mb-6">
            Units of Measure
          </h2>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* Add/Edit Form */}
          <form onSubmit={handleSubmit} className="mb-6 space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-700">
                {editingId ? 'Edit Unit' : 'Add New Unit'}
              </h3>
              {editingId && (
                <Button
                  type="button"
                  variant="outlined"
                  color="gray"
                  onClick={cancelForm}
                  className="flex items-center shadow-none rounded-none"
                >
                  <FaTimes className="mr-2" /> Cancel
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                type="text"
                label="Code *"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                className="uppercase"
                style={{ textTransform: 'uppercase' }}
                required
              />
              <Input
                type="text"
                label="Name *"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
              <Input
                type="text"
                label="Abbreviation *"
                value={form.abbreviation}
                onChange={(e) => setForm({ ...form, abbreviation: e.target.value })}
                required
              />
            </div>
            <Button
              type="submit"
              className="bg-brand-primary text-white flex items-center shadow-none rounded-none"
            >
              <FaPlus className="mr-2" /> {editingId ? 'Update Unit' : 'Add Unit'}
            </Button>
          </form>

          {/* Items Table */}
          {loading ? (
            <Typography>Loading...</Typography>
          ) : (
            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <table className="w-full table-auto border-collapse min-w-[800px]">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">ID</th>
                    <th className="border px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Code</th>
                    <th className="border px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Name</th>
                    <th className="border px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Abbreviation</th>
                    <th className="border px-2 sm:px-4 py-2 text-center text-xs sm:text-sm">Status</th>
                    <th className="border px-2 sm:px-4 py-2 text-center text-xs sm:text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="border px-2 sm:px-4 py-2">
                        <Typography className="text-xs sm:text-sm">{item.id}</Typography>
                      </td>
                      <td className="border px-2 sm:px-4 py-2">
                        <Typography className="uppercase text-xs sm:text-sm">{item.code}</Typography>
                      </td>
                      <td className="border px-2 sm:px-4 py-2">
                        <Typography className="text-xs sm:text-sm">{item.name}</Typography>
                      </td>
                      <td className="border px-2 sm:px-4 py-2">
                        <Typography className="uppercase text-xs sm:text-sm">{item.abbreviation}</Typography>
                      </td>
                      <td className="border px-2 sm:px-4 py-2 text-center">
                        <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-none text-xs font-medium ${item.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                          }`}>
                          {item.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="border px-2 sm:px-4 py-2 text-center">
                        <div className="flex flex-wrap gap-1 sm:gap-2 justify-center">
                          <Button
                            onClick={() => startEdit(item)}
                            className="bg-blue-600 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs shadow-none rounded-none"
                            size="sm"
                            disabled={editingId === item.id}
                          >
                            <FaPenAlt className="w-2.5 sm:w-3 h-2.5 sm:h-3" />
                          </Button>
                          <Button
                            onClick={() => deleteItem(item.id)}
                            className="bg-red-600 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs shadow-none rounded-none"
                            size="sm"
                            disabled={editingId === item.id}
                          >
                            <FaTrash className="w-2.5 sm:w-3 h-2.5 sm:h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default UnitsOfMeasure;

